/**
 * Which side of the app someone who is BOTH a member and a coach is using.
 *
 * Member view is the default: a member who later became a coach still lands in
 * the member app and switches to the coach view on purpose. Stored per user per
 * device -- it's a convenience, not a permission, so losing it (private window,
 * cleared storage) just puts them back in the member view.
 */
export type ActiveView = 'member' | 'coach';

const key = (userId: string) => `bc.activeView.${userId}`;

export const getStoredActiveView = (userId: string): ActiveView => {
  try {
    return localStorage.getItem(key(userId)) === 'coach' ? 'coach' : 'member';
  } catch {
    return 'member';
  }
};

export const storeActiveView = (userId: string, view: ActiveView) => {
  try {
    localStorage.setItem(key(userId), view);
  } catch {
    // Storage blocked: the choice just won't survive a reload.
  }
};

/** Where each view starts. Same destinations the login role picker used. */
export const ACTIVE_VIEW_HOME: Record<ActiveView, string> = {
  member: '/scans',
  coach: '/trainer-profile',
};

/**
 * Where to land when switching back to a view: the page they were on when
 * they last left it, so going coach -> member -> coach returns them to the
 * same place instead of the view's home (for a member with no scans yet,
 * that home is the Pod sign-up pitch). Per tab, so a fresh visit still
 * starts at the view's home.
 */
const pathKey = (view: ActiveView) => `bc.viewPath.${view}`;

export const rememberViewPath = (view: ActiveView, path: string) => {
  try {
    sessionStorage.setItem(pathKey(view), path);
  } catch {
    // Storage blocked: switching just goes to the view's home.
  }
};

export const returnPathFor = (view: ActiveView): string => {
  try {
    return sessionStorage.getItem(pathKey(view)) || ACTIVE_VIEW_HOME[view];
  } catch {
    return ACTIVE_VIEW_HOME[view];
  }
};
